import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditorRef } from 'platejs/react';
import { setTableColSize } from '@platejs/table';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import clsx from 'clsx';
import styles from './tableElements.module.scss';
import { useTableHover } from './tableHoverContext';

// Notion-style 6-dot drag handles for table rows and columns.
//
// Rendered ONCE per table (not per cell) and PORTALLED to document.body so
// they don't take up any DOM space around the table. The earlier wrapper-
// based approach reserved 26px of padding around the table for the handles,
// which collapsed into negative margin compensation — that worked at the
// editor root but pushed the table 26px past its container's left edge
// when nested inside a column-layout column, visually overlapping the
// column separator. Portal sidesteps the issue entirely: handles float on
// top of the document at viewport coordinates derived from the table's
// getBoundingClientRect().
//
// Hover state is supplied by TableHoverContext — each Cell calls
// show(rowIdx, colIdx) on mouseenter. Handles cancel the deferred hide on
// their own mouseenter so the pointer can travel from cell border to
// floating handle without flicker.
//
// CLICK → selects the entire row/column via the same Slate range-selection
// trick the right-click menu's "Select row/column" uses.
//
// DRAG → reorders the row/column using Slate's moveNodes. Column drag also
// swaps the persisted colSize so the moved column keeps its width in the
// new slot (via @platejs/table's setTableColSize). Rows have no equivalent
// persisted size mechanism; row height lives on the row node itself and
// moves automatically with moveNodes.

interface Props {
    tableElement: any;
    tableRef: React.RefObject<HTMLTableElement | null>;
}

interface DragState {
    axis: 'row' | 'column';
    sourceIndex: number;
    // Live target BOUNDARY index (between elements). 0 = before first,
    // length = after last. Used for the insertion indicator and to compute
    // the final moveNodes destination on drop.
    targetBoundary: number;
    // Pixel coord of the indicator line (in viewport coords) recomputed
    // every pointermove.
    indicatorPx: number;
    // Indicator length: table width for row drag, table height for column.
    indicatorLength: number;
}

const HANDLE_OFFSET = 22; // px outside the table edge

export function TableHandlesOverlay({ tableElement, tableRef }: Props) {
    const editor = useEditorRef();
    const { hovered, hideDeferred, cancelHide, clearImmediately } = useTableHover();
    const [dragState, setDragState] = useState<DragState | null>(null);
    const dragStateRef = useRef<DragState | null>(null);
    dragStateRef.current = dragState;

    // Force a re-position when the user scrolls or resizes the window —
    // since handles render in viewport coords via getBoundingClientRect,
    // any change in the table's screen position needs a re-measure. Cheap
    // because we only repaint a couple of small elements.
    const [, forceTick] = useState(0);
    useEffect(() => {
        if (hovered.row == null && hovered.col == null && !dragState) return;
        const onScrollOrResize = () => forceTick((t) => t + 1);
        // capture:true catches scrolls on inner scroll containers too
        // (editor body, modal, etc.).
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [hovered.row, hovered.col, dragState]);

    // Resolve the table's slate path. Findable as long as tableElement is
    // still in the editor tree.
    const tablePath: number[] | null = useMemo(() => {
        try {
            const path = (editor as any)?.api?.findPath?.(tableElement);
            return Array.isArray(path) ? path : null;
        } catch {
            return null;
        }
    }, [editor, tableElement]);

    // Read DOM rows/cells live each render. Tables are small; this is cheap
    // and avoids stale measurements if the cell DOM mutated.
    const rows = tableRef.current?.querySelectorAll<HTMLTableRowElement>(':scope > tbody > tr') ?? null;
    const rowCount = rows?.length ?? 0;
    const firstRowCells = rows?.[0]?.querySelectorAll<HTMLTableCellElement>(':scope > td, :scope > th') ?? null;
    const colCount = firstRowCells?.length ?? 0;

    // VIEWPORT-coordinate measurements (since handles are portalled to body
    // and use position: fixed). No wrapper offset arithmetic — straight from
    // getBoundingClientRect.
    const positions = useMemo(() => {
        if (!tableRef.current) return null;
        const tableRect = tableRef.current.getBoundingClientRect();
        const rowsArr = rows ? Array.from(rows) : [];
        const cellsArr = firstRowCells ? Array.from(firstRowCells) : [];
        return {
            tableLeft: tableRect.left,
            tableTop: tableRect.top,
            tableRight: tableRect.right,
            tableBottom: tableRect.bottom,
            tableWidth: tableRect.width,
            tableHeight: tableRect.height,
            rowCenters: rowsArr.map((r) => {
                const rr = r.getBoundingClientRect();
                return rr.top + rr.height / 2;
            }),
            rowEdges: [tableRect.top, ...rowsArr.map((r) => r.getBoundingClientRect().bottom)],
            colCenters: cellsArr.map((c) => {
                const cr = c.getBoundingClientRect();
                return cr.left + cr.width / 2;
            }),
            colEdges: [tableRect.left, ...cellsArr.map((c) => c.getBoundingClientRect().right)],
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hovered.row, hovered.col, dragState, rowCount, colCount, tableRef]);

    const selectAxis = useCallback(
        (axis: 'row' | 'column', index: number) => {
            if (!tablePath) return;
            try {
                const ed: any = editor;
                const tableNode: any = ed?.api?.node?.(tablePath)?.[0];
                if (!tableNode?.children?.length) return;
                let startPath: number[];
                let endPath: number[];
                if (axis === 'row') {
                    const row = tableNode.children[index];
                    if (!row?.children?.length) return;
                    startPath = [...tablePath, index, 0];
                    endPath = [...tablePath, index, row.children.length - 1];
                } else {
                    startPath = [...tablePath, 0, index];
                    endPath = [...tablePath, tableNode.children.length - 1, index];
                }
                const start = ed?.api?.start?.(startPath);
                const end = ed?.api?.end?.(endPath);
                if (start && end) {
                    ed?.tf?.select?.({ anchor: start, focus: end });
                    ed?.tf?.focus?.();
                }
            } catch {
                /* defensive */
            }
        },
        [editor, tablePath],
    );

    const pointerToBoundary = useCallback(
        (axis: 'row' | 'column', clientX: number, clientY: number): number => {
            if (axis === 'row') {
                const centers = positions?.rowCenters ?? [];
                let boundary = centers.length;
                for (let i = 0; i < centers.length; i++) {
                    if (clientY < centers[i]) {
                        boundary = i;
                        break;
                    }
                }
                return boundary;
            } else {
                const centers = positions?.colCenters ?? [];
                let boundary = centers.length;
                for (let i = 0; i < centers.length; i++) {
                    if (clientX < centers[i]) {
                        boundary = i;
                        break;
                    }
                }
                return boundary;
            }
        },
        [positions],
    );

    const boundaryToPx = useCallback(
        (axis: 'row' | 'column', boundary: number): number => {
            if (axis === 'row') {
                const edges = positions?.rowEdges ?? [];
                return edges[Math.min(boundary, edges.length - 1)] ?? 0;
            } else {
                const edges = positions?.colEdges ?? [];
                return edges[Math.min(boundary, edges.length - 1)] ?? 0;
            }
        },
        [positions],
    );

    const commitReorder = useCallback(
        (axis: 'row' | 'column', src: number, boundary: number) => {
            if (!tablePath) return;
            if (boundary === src || boundary === src + 1) return; // no-op
            const dst = boundary > src ? boundary - 1 : boundary;
            const ed: any = editor;
            try {
                if (axis === 'row') {
                    ed?.tf?.moveNodes?.({ at: [...tablePath, src], to: [...tablePath, dst] });
                } else {
                    const tableNode: any = ed?.api?.node?.(tablePath)?.[0];
                    const rowCountLocal = tableNode?.children?.length ?? 0;
                    const colSizes: number[] = Array.isArray(tableNode?.colSizes) ? [...tableNode.colSizes] : [];
                    ed?.tf?.withoutNormalizing?.(() => {
                        for (let r = 0; r < rowCountLocal; r++) {
                            ed?.tf?.moveNodes?.({ at: [...tablePath, r, src], to: [...tablePath, r, dst] });
                        }
                    });
                    if (colSizes.length > 0) {
                        const [moved] = colSizes.splice(src, 1);
                        colSizes.splice(dst, 0, moved);
                        // `{ at: tablePath }` pins the transform to THIS
                        // table — without it the lib falls back to "table
                        // above editor selection", which during a drag may
                        // be undefined or wrong.
                        for (let i = 0; i < colSizes.length; i++) {
                            const w = colSizes[i];
                            if (typeof w === 'number' && w > 0) {
                                try {
                                    (setTableColSize as any)(ed, { colIndex: i, width: w }, { at: tablePath });
                                } catch {
                                    /* per-slot defensive */
                                }
                            }
                        }
                    }
                }
            } catch {
                /* defensive — table mutated mid-drag */
            }
        },
        [editor, tablePath],
    );

    useEffect(() => {
        if (!dragState) return;
        const onMove = (e: PointerEvent) => {
            const ds = dragStateRef.current;
            if (!ds) return;
            const boundary = pointerToBoundary(ds.axis, e.clientX, e.clientY);
            const indicatorPx = boundaryToPx(ds.axis, boundary);
            setDragState((prev) => (prev ? { ...prev, targetBoundary: boundary, indicatorPx } : prev));
        };
        const onUp = (e: PointerEvent) => {
            const ds = dragStateRef.current;
            if (!ds) return;
            const boundary = pointerToBoundary(ds.axis, e.clientX, e.clientY);
            commitReorder(ds.axis, ds.sourceIndex, boundary);
            setDragState(null);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            clearImmediately();
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
        };
    }, [dragState, pointerToBoundary, boundaryToPx, commitReorder, clearImmediately]);

    const startDrag = useCallback(
        (axis: 'row' | 'column', index: number, e: React.PointerEvent) => {
            e.preventDefault();
            e.stopPropagation();
            selectAxis(axis, index);
            const boundary = pointerToBoundary(axis, e.clientX, e.clientY);
            setDragState({
                axis,
                sourceIndex: index,
                targetBoundary: boundary,
                indicatorPx: boundaryToPx(axis, boundary),
                indicatorLength: axis === 'row' ? positions?.tableWidth ?? 0 : positions?.tableHeight ?? 0,
            });
            document.body.style.cursor = 'grabbing';
            document.body.style.userSelect = 'none';
        },
        [selectAxis, pointerToBoundary, boundaryToPx, positions],
    );

    if (!positions || rowCount === 0 || colCount === 0) return null;
    if (typeof document === 'undefined') return null;

    const showColHandle = hovered.col != null && hovered.col < colCount && !dragState;
    const showRowHandle = hovered.row != null && hovered.row < rowCount && !dragState;

    const colHandle =
        showColHandle && positions.colCenters[hovered.col!] != null ? (
            <button
                type="button"
                className={clsx(styles.tableHandle, styles.tableHandleCol)}
                style={{
                    position: 'fixed',
                    left: positions.colCenters[hovered.col!] - 14,
                    top: positions.tableTop - HANDLE_OFFSET,
                }}
                onMouseEnter={cancelHide}
                onMouseLeave={hideDeferred}
                onClick={(e) => {
                    e.preventDefault();
                    selectAxis('column', hovered.col!);
                }}
                onPointerDown={(e) => {
                    if (e.button !== 0) return;
                    if (colCount > 1) startDrag('column', hovered.col!, e);
                }}
                title="Click to select column · Drag to reorder"
                aria-label={`Column ${hovered.col! + 1} handle`}
            >
                <DragIndicatorIcon style={{ fontSize: 16, transform: 'rotate(90deg)' }} />
            </button>
        ) : null;

    const rowHandle =
        showRowHandle && positions.rowCenters[hovered.row!] != null ? (
            <button
                type="button"
                className={clsx(styles.tableHandle, styles.tableHandleRow)}
                style={{
                    position: 'fixed',
                    left: positions.tableLeft - HANDLE_OFFSET,
                    top: positions.rowCenters[hovered.row!] - 14,
                }}
                onMouseEnter={cancelHide}
                onMouseLeave={hideDeferred}
                onClick={(e) => {
                    e.preventDefault();
                    selectAxis('row', hovered.row!);
                }}
                onPointerDown={(e) => {
                    if (e.button !== 0) return;
                    if (rowCount > 1) startDrag('row', hovered.row!, e);
                }}
                title="Click to select row · Drag to reorder"
                aria-label={`Row ${hovered.row! + 1} handle`}
            >
                <DragIndicatorIcon style={{ fontSize: 16 }} />
            </button>
        ) : null;

    const indicator =
        dragState != null ? (
            <div
                className={clsx(
                    styles.tableDropIndicator,
                    dragState.axis === 'row' ? styles.tableDropIndicatorRow : styles.tableDropIndicatorCol,
                )}
                style={
                    dragState.axis === 'row'
                        ? {
                              position: 'fixed',
                              left: positions.tableLeft,
                              top: dragState.indicatorPx - 1,
                              width: positions.tableWidth,
                              height: 2,
                          }
                        : {
                              position: 'fixed',
                              left: dragState.indicatorPx - 1,
                              top: positions.tableTop,
                              width: 2,
                              height: positions.tableHeight,
                          }
                }
            />
        ) : null;

    return createPortal(
        <>
            {colHandle}
            {rowHandle}
            {indicator}
        </>,
        document.body,
    );
}
